import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Unavailable } from '../components/Unavailable';
import { partnerGateway } from '../services';
import { useLoad } from '../state/useLoad';
import { usePartner } from '../state/usePartner';
import type { PartnerClient } from '../types';
import { attention } from './attention';

export function PartnerDashboardPage() {
  const { membership } = usePartner();
  const { data, error, loading } = useLoad((signal) => partnerGateway.listClients(membership.partnerOrganizationId, signal), membership.partnerOrganizationId, 'Your clients could not be loaded.');
  const clients = data ?? [];
  const count = (...s: PartnerClient['status'][]) => clients.filter((c) => s.includes(c.status)).length;
  // Read once per visit: the time is an input, not something to recompute on every render.
  const [now] = useState(() => Date.now());
  const needs = attention(clients, now);

  return (
    <>
      <h1>Portfolio overview</h1>
      {error && <p role="alert" className="pp-error">{error}</p>}
      {loading && !data && <p className="pp-muted">Loading…</p>}

      {data && (
        <>
          <section aria-labelledby="pp-portfolio" className="pp-section">
            <h2 id="pp-portfolio">Your clients</h2>
            <div className="pp-cards">
              <Card label="Active clients" value={count('Active')} to="/partner/clients?status=Active" />
              <Card label="Clients onboarding" value={count('Onboarding')} to="/partner/clients?status=Onboarding" />
              <Card label="Awaiting client acceptance" value={count('InvitationSent', 'AwaitingClientAcceptance')} to="/partner/clients?status=pending" />
              <Card label="Needing attention" value={needs.length} to="#pp-attention" />
            </div>
          </section>

          <section aria-labelledby="pp-attention-h" className="pp-section" id="pp-attention">
            <h2 id="pp-attention-h">Needing attention</h2>
            {needs.length === 0 ? (
              <p className="pp-muted">Nothing needs attention right now.</p>
            ) : (
              <ul className="pp-list">
                {needs.map(({ client, why }) => (
                  <li key={`${client.id}-${why}`}>
                    <Link to={`/partner/clients/${client.id}`}>{client.clientCompanyName ?? client.clientName}</Link> · {why}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="pp-payroll" className="pp-section">
            <h2 id="pp-payroll">Payroll across your clients</h2>
            <Unavailable>
              Employees paid, payrolls scheduled, awaiting preparation, awaiting approval and completed this month all need access to a client company, which is not built yet. None of these is shown as zero, because zero
              would be a claim.
            </Unavailable>
          </section>

          <section aria-labelledby="pp-deadlines" className="pp-section">
            <h2 id="pp-deadlines">Upcoming work, exceptions and filing dates</h2>
            <Unavailable>Payroll deadlines, funding and tax deposit dates, filing dates and payroll exceptions come from the clients' own payroll data, which the portal cannot read yet.</Unavailable>
          </section>

          <section aria-labelledby="pp-revenue" className="pp-section">
            <h2 id="pp-revenue">Revenue</h2>
            <Unavailable>Partner revenue needs approved commercial agreements and billing records, which are not built yet. Client payroll volume is never shown here as your revenue.</Unavailable>
          </section>
        </>
      )}
    </>
  );
}

function Card({ label, value, to }: { label: string; value: number; to: string }) {
  const body = (
    <>
      <span className="pp-card-value">{value}</span>
      <span className="pp-card-label">{label}</span>
    </>
  );
  return to.startsWith('#') ? (
    <a className="pp-stat" href={to}>{body}</a>
  ) : (
    <Link className="pp-stat" to={to}>{body}</Link>
  );
}
